import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrderDetail } from '@reikraft/shared';
import { useToast } from '@reikraft/ui';
import { api, errorText } from '../api';

export const orderKey = (id: string) => ['order', id] as const;

export function useOrder(id: string) {
  return useQuery({ queryKey: orderKey(id), queryFn: () => api.get<OrderDetail>(`/admin/orders/${id}`) });
}

/**
 * Действие с заказом: API возвращает свежую карточку, кладём её в кэш
 * и обновляем списки и счётчики.
 */
export function useOrderAction<V = void>(
  id: string,
  fn: (v: V) => Promise<OrderDetail | { order: OrderDetail }>,
  success?: string | ((o: OrderDetail) => string),
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      const order = 'order' in res ? res.order : res;
      qc.setQueryData(orderKey(id), order);
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['counters'] });
      void qc.invalidateQueries({ queryKey: ['stock'] });
      if (success) toast(typeof success === 'string' ? success : success(order));
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });
}

export const orderApi = {
  status: (id: string, to: string, reason?: string) => api.post<OrderDetail>(`/admin/orders/${id}/status`, { to, reason: reason ?? null }),
};
